// Split out of src/components/Wizard.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). Wizard.jsx
// re-exports everything it exported before, so existing imports keep working.

import { CoinIcon, Icon, ChildMark } from "../Common.jsx";
import { learnEarnSummary } from "../LearningAdminTab.jsx";
import { NotificationToggle, requestPushSubscription } from "../Onboarding.jsx";
import { NotificationGroup } from "../settings/Notifications.jsx";
import { ownerOf } from "../../lib/adults.js";
import { TokenSetupFields } from "../SettingsTab.jsx";
import { CURRENCY_SYMBOL, DEFAULT_SPENDING_CAP_GBP, tokenValueParts } from "../../lib/constants.js";
import { fmtPence, resolveStarterSets, spendingCapPence } from "../../lib/utils.js";
import { ChildContextBar } from "../ChildContextBar.jsx";
import { EarningsHealthLine } from "../EarningsHealthLine.jsx";
import { seedHouseholdFromCaps } from "../../lib/spendingDefaults.js";
import { isYoungChild } from "../../lib/age.js";
import { t as ct } from "../../lib/content.js";
import { WizardIntro, WizardFooter, WizardBackButton, CAP_PRESETS } from "./wizardShared.jsx";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

export function PinSetupStep({ state, update, setPendingVerifiedPin, setVerifiedPin, showToast, onBack }) {
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [error, setError] = useState("");

  function save() {
    if (!/^\d{6}$/.test(pin)) { setError("PIN must be exactly 6 digits."); return; }
    if (pin !== confirmPin) { setError("PINs don't match."); return; }
    if (pin === "123456") { setError("Please choose something other than the starter PIN."); return; }
    setError("");
    // A brand-new household's real PIN is always the "123456" default until changed here —
    // this proves to the server we know the current PIN, same as SettingsTab's savePin().
    setPendingVerifiedPin("123456");
    // The household creator's session never touches the PinPad (see App.jsx's justCreated
    // bypass), so App's own `verifiedPin` state would otherwise stay null for the rest of
    // this session even after a real PIN is set here — silently breaking anything gated on
    // it later (e.g. reveal-passcode in Settings) with a 403, despite the parent clearly
    // just having proven they know the PIN by setting it. Setting it here closes that gap.
    setVerifiedPin(pin);
    // LB-340: the PIN lives on the adult now, not on settings. This is the household's
    // creator, so it is the Owner's — ownerOf() reads the row the worker's migration made
    // (or, for a household created since, the row that came back with the first GET). The
    // proof above is unchanged: "123456" is the placeholder every new household starts on,
    // and the save path checks it against that same row.
    //
    // If there is no Owner row to write to, this must NOT report success. The first cut set
    // pinConfigured: true regardless, so a household in that state was told "Parent PIN set"
    // while the PIN went nowhere — and the parent then could not get into Parent Mode with
    // the PIN they had just chosen. Principle 5: never leave a parent holding the bag for
    // something the app decided. It cannot happen against the real worker (every GET carries
    // the adults list, and creation seeds it in Root.jsx), so this is a guard on a state that
    // should be unreachable rather than a path with a UI of its own.
    // Checked HERE, against the current state, and NOT from inside the updater. update()
    // wraps fn in setState((prev) => ...), so React defers it: a flag set inside fn is
    // still false on the next line. The first cut read one there and so ALWAYS took the
    // failure branch — the PIN was written (later, correctly) while the parent was told it
    // had not been. That is principle 5 broken harder than the bug it was meant to fix, and
    // it reached the production gate. Whether there is an Owner to write to is knowable
    // from the state we already hold, so ask before writing.
    if (!ownerOf(state)) {
      showToast(ct("toast.pin_not_set", "Couldn't set the PIN just now. Check your connection and try again"), "bad");
      return;
    }
    update((s) => {
      const owner = ownerOf(s);
      if (!owner) return null; // belt and braces: no write, no revision bump, no autosave
      owner.pin = pin;
      // PIN before Notifications (Alan, 20 Sep): the PIN is now the step before the last, so
      // setting it moves on to Notifications (6). Setup still cannot finish without a PIN.
      s.settings.pinConfigured = true; s.onboardingStep = 6; return s;
    });
    showToast(ct("toast.wizard_pin_set", "Parent PIN set"), "good");
  }

  return (
    <>
      {/* Alan's wording, 20 Sep. The ask is its own key so it can sit on its own line. */}
      <WizardIntro
        icon={<Icon id="ic-pin" size={30} />}
        title={ct("wizard.pin.title", "Protect your household with a PIN")}
        body={ct("wizard.pin.body", "Access to your children's data, household settings and approvals is through Parent Mode, which is protected by a 6-digit PIN.")}
      />
      {/* The ask sits with the fields it is asking about (Alan, 21 Sep), not as a tail on the
          explanation above. Same key and same testid — this moved, it did not change. */}
      <p className="text-sm text-slate-600 mb-2" data-testid="wizard-pin-ask">{ct("wizard.pin.body_ask", "Please set your PIN now.")}</p>
      <div className="bg-white rounded-lg p-4 border border-slate-200">
        <div className="flex items-center gap-2 mb-2">
          <label htmlFor="wizard-pin" className="text-xs font-medium text-slate-500 w-28 flex-shrink-0">{ct("wizard.pin.set_label", "Set your PIN")}</label>
          <input id="wizard-pin" className="border border-slate-200 rounded-lg px-3 py-2 text-sm flex-1 min-w-0" type="password" inputMode="numeric" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} />
        </div>
        <div className="flex items-center gap-2 mb-2">
          <label htmlFor="wizard-pin-confirm" className="text-xs font-medium text-slate-500 w-28 flex-shrink-0">{ct("wizard.pin.confirm_label", "Confirm PIN")}</label>
          <input id="wizard-pin-confirm" className="border border-slate-200 rounded-lg px-3 py-2 text-sm flex-1 min-w-0" type="password" inputMode="numeric" maxLength={6} value={confirmPin} onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))} />
        </div>
        {error && <div className="text-rose-600 text-base font-semibold mb-2">{error}</div>}
        <button className="w-full bg-slate-900 text-white font-bold text-sm rounded-lg py-2.5" onClick={save}>{ct("wizard.pin.submit", "Set PIN & Continue")}</button>
      </div>
      {/* Back from here returns to the last child's Learn & Earn (or the review on the auto
          route). Nothing ever goes BACK INTO this step once a PIN exists — backTargetFor
          skips it — because re-entering it would send a proof-of-starter-PIN the server
          rejects. */}
      <WizardFooter>
        <WizardBackButton onBack={onBack} />
      </WizardFooter>
    </>
  );
}



/* ------------------------- weekly spending cap (step 3) -------------------------
 * The cap has existed and been ON at £5/week for every child since Aug 2026 — but the
 * wizard never mentioned it, so parents met it for the first time as a wall their child
 * hit mid-week. Worse, nothing related it to reward VALUES: a household could finish setup
 * with a spelling test paying more than a whole week's cap and no hint anything was wrong.
 *
 * It belongs on the token step because it is the second half of one decision. "What is a
 * token worth" and "how many can they earn" are the same question asked twice, and the
 * answers have to be given together for either to mean anything.
 *
 * The global/per-child question is asked outright rather than inferred (Alan, 30 Aug).
 * Inferring it would mean either silently giving a 6-year-old and a 12-year-old the same
 * allowance, or silently not — both are decisions worth one tap of a parent's attention.
 */
export function SpendingCapStep({ state, update, compact = false }) {
  const mode = state.settings.capMode === "perChild" ? "perChild" : "global";
  const children = state.profiles || [];
  const first = children[0];
  const globalValue = first?.spendingCap?.weeklyGBP ?? DEFAULT_SPENDING_CAP_GBP;
  const [customOpen, setCustomOpen] = useState(!CAP_PRESETS.includes(globalValue));

  function setMode(next) {
    update((s) => { s.settings.capMode = next; return s; });
  }
  function setCapForAll(gbp) {
    update((s) => {
      s.profiles.forEach((p) => { p.spendingCap = { ...(p.spendingCap || {}), enabled: true, weeklyGBP: gbp }; });
      return s;
    });
  }
  function setCapFor(childId, raw) {
    const n = parseFloat(raw);
    if (!n || n <= 0) return;
    update((s) => {
      const c = s.profiles.find((p) => p.id === childId);
      if (!c) return null;
      c.spendingCap = { ...(c.spendingCap || {}), enabled: true, weeklyGBP: Math.round(n * 100) / 100 };
      return s;
    });
  }

  return (
    <div className="bg-white rounded-lg p-4 border border-slate-200" data-testid="wizard-cap-card">
      <div className={`flex items-center gap-2.5 ${compact ? "mb-3" : "mb-1.5"}`}>
        <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0"><Icon id="ic-cap" size={18} hue="gold" /></div>
        <div className="text-sm font-bold text-slate-900">{compact ? ct("wizard.money.cap_heading", "Most they can earn a week") : ct("wizard.tokens.cap_title", "Weekly spending cap")}</div>
      </div>
      {!compact && (
        <p className="text-sm text-slate-500 leading-snug mb-3">
          {ct("wizard.tokens.cap_body", "The most a child can earn in a week — already switched on, so nobody can blow the budget by accident. We'll size their reward amounts to fit it.")}
        </p>
      )}

      {children.length > 1 && compact && (
        <p className="text-xs font-medium text-slate-500 mb-3" data-testid="cap-mode-line">
          {mode === "global"
            ? <>{ct("wizard.money.cap_same", "Same for every child")} · <button type="button" data-testid="cap-mode-per-child" className="text-emerald-700 underline" onClick={() => setMode("perChild")}>{ct("wizard.money.cap_each_link", "a cap each")}</button></>
            : <>{ct("wizard.money.cap_each", "A cap each")} · <button type="button" data-testid="cap-mode-global" className="text-emerald-700 underline" onClick={() => setMode("global")}>{ct("wizard.money.cap_same_link", "same for every child")}</button></>}
        </p>
      )}
      {children.length > 1 && !compact && (
        <div className="flex bg-slate-100 rounded-xl p-1 gap-1 mb-3" role="group" aria-label="Cap mode">
          <button type="button" data-testid="cap-mode-global" onClick={() => setMode("global")}
            className={`flex-1 text-xs font-bold rounded-lg py-2 ${mode === "global" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
            {ct("wizard.tokens.cap_mode_global", "Same for every child")}
          </button>
          <button type="button" data-testid="cap-mode-per-child" onClick={() => setMode("perChild")}
            className={`flex-1 text-xs font-bold rounded-lg py-2 ${mode === "perChild" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
            {ct("wizard.tokens.cap_mode_per_child", "A cap each")}
          </button>
        </div>
      )}

      {mode === "global" || children.length <= 1 ? (
        <>
          <div className="grid grid-cols-5 gap-2">
            {CAP_PRESETS.map((v) => (
              <button key={v} type="button" data-testid="cap-preset"
                onClick={() => { setCustomOpen(false); setCapForAll(v); }}
                className={`text-sm font-bold rounded-xl px-3.5 py-2 border ${!customOpen && globalValue === v ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-500"}`}>
                {CURRENCY_SYMBOL}{v}
              </button>
            ))}
            <button type="button" onClick={() => setCustomOpen(true)}
              className={`text-sm font-bold rounded-xl px-3.5 py-2 border ${customOpen ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-500"}`}>
              {ct("wizard.tokens.cap_other", "Other")}
            </button>
          </div>
          {customOpen && (
            <div className="flex items-center gap-2 mt-2.5">
              <label className="text-xs font-medium text-slate-500">{CURRENCY_SYMBOL} {ct("wizard.tokens.cap_per_week", "per week")}</label>
              <input className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm w-24 text-right font-bold" type="number" min="0.01" step="any"
                data-testid="cap-custom-input"
                defaultValue={globalValue}
                onBlur={(e) => { const n = parseFloat(e.target.value); if (n > 0) setCapForAll(Math.round(n * 100) / 100); }} />
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col gap-2" data-testid="cap-per-child-rows">
          {children.map((c) => (
            <div key={c.id} className="flex items-center gap-2.5">
              <ChildMark profile={c} size="sm" />
              <span className="text-sm font-semibold text-slate-700 flex-1 min-w-0 truncate">{c.name}</span>
              <label className="text-xs font-medium text-slate-400">{CURRENCY_SYMBOL}/wk</label>
              <input className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm w-20 text-right font-bold" type="number" min="0.01" step="any"
                data-testid="cap-child-input"
                defaultValue={c.spendingCap?.weeklyGBP ?? DEFAULT_SPENDING_CAP_GBP}
                onBlur={(e) => setCapFor(c.id, e.target.value)} />
            </div>
          ))}
          <p className="text-xs text-slate-400 font-medium">{ct("wizard.tokens.cap_per_child_note", "Each child's reward amounts will be sized to their own cap.")}</p>
        </div>
      )}
    </div>
  );
}

/* ------------------------- money (step 3), Option A (Alan, 4 Sep 2026) -------------------------
 * The step had grown to five cards and about two hundred words in front of two controls:
 * an intro, the token value with two paragraphs, the cap with a paragraph, a folded vault
 * and Money Monday explainer, and the fork with its own paragraph. There are only two
 * decisions here and both must be seen together — a cap means nothing until you know what
 * a token is worth — so an accordion would hide one behind the other. Instead:
 *
 * - The card headings are the questions ("One token is worth", "Most they can earn a week").
 * - A LIVE SUMMARY LINE replaces the explanations: "£5 a week at 10p is 50 tokens — that's
 *   what Alex's tasks, streak and games will add up to." It changes as the parent taps, so
 *   the relationship is learned by using it, and it is the exact figure the review shows next.
 * - The vault and Money Monday explainers leave the step. They are not decisions and not
 *   about this step (Money Monday is off by default and lives in Settings); they belong on
 *   the "all set up" overlay or in Help. Their CMS keys stay published but are no longer read.
 * - The fork loses its paragraph and gains one sentence saying what comes next.
 *
 * Kept: the same-for-everyone / a-cap-each choice, and the rescale prompt when the rate
 * changes after rewards exist (a safeguard, not copy). No emoji anywhere on the step.
 */
export function TokenSetupStep({ state, update, showToast, goToStep, onBack, onChooseRoute }) {
  const hasSeenLaterSteps = (state.onboardingStep || 1) > 3 || !!state.settings._wizardPastTokens;
  const [rescalePrompt, setRescalePrompt] = useState(null);
  const children = state.profiles || [];
  const rate = state.settings.exchangeRate > 0 ? state.settings.exchangeRate : 10;
  const first = children[0];
  const perChild = state.settings.capMode === "perChild" && children.length > 1;
  const capGbp = first?.spendingCap?.weeklyGBP ?? DEFAULT_SPENDING_CAP_GBP;
  const capTokens = Math.round(capGbp * rate);
  const pence = `${tokenValueParts(rate).pence}p`;
  const kidsPhrase = children.length === 0
    ? ct("wizard.money.summary_kids_none", "a child")
    : children.length === 1
      ? children[0].name
      : perChild
        ? ct("wizard.money.summary_kids_each", "each child")
        : ct("wizard.money.summary_kids_all", "each of your {n} children", { n: children.length });
  return (
    <>
      <WizardIntro
        icon={<Icon id="ic-banknote" size={30} />}
        title={ct("wizard.money.title", "Money")}
        body={ct("wizard.money.body", "The token exchange rate and the weekly cap set the scale for everything else.")}
        context={<>
          <div className="text-sm text-slate-700 leading-snug" data-testid="money-summary">
        {perChild
          ? ct("wizard.money.summary_per_child", "At {pence} a token, each child's cap sets what their tasks, streak and games add up to.", { pence })
          : ct("wizard.money.summary", "{cap} a week at {pence} a token is {tokens} tokens — that's what {kids}'s tasks, streak and games will add up to.", { cap: fmtPence(Math.round(capGbp * 100)), pence, tokens: capTokens, kids: kidsPhrase })}
      </div>
        </>}
      />
      <TokenSetupFields state={state} update={update} showToast={showToast} seedDefaults compact confirmRescale={hasSeenLaterSteps ? setRescalePrompt : null} />
      <SpendingCapStep state={state} update={update} compact />


      {/* Pass V.1 (Alan): "Now the rest" is anchored to the Kid setup panel — its own
          left-column item on the panel's grid row (see .wizard-fork-note in tokens.css);
          on a phone it sits directly above the panel. */}
      <div className="wizard-fork-note px-0.5">
        <div className="flex items-center gap-2.5 mb-1"><div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0"><Icon id="ic-choice" size={20} /></div><div className="text-base font-extrabold text-slate-900">{ct("wizard.fork.heading_short", "Now the rest")}</div></div>
        <p className="text-sm text-slate-500 leading-snug">{children.length === 1
          ? ct("wizard.fork.next_one", "All that's left is {name}'s daily habits, earning tasks and Learn & Earn games.", { name: children[0].name })
          : ct("wizard.fork.next_many", "All that's left is each child's daily habits, earning tasks and Learn & Earn games.")}</p>
      </div>
      <SetupFork
        childCount={children.length}
        autoLabel={children.length === 1
          ? ct("wizard.fork.auto_one", "Set {name} up for me", { name: children[0].name })
          : ct("wizard.fork.auto_many", "Set them up for me")}
        onBack={onBack}
        onChoose={(route) => { update((s2) => { s2.settings._wizardPastTokens = true; return s2; }); onChooseRoute(route); }}
      />
      {rescalePrompt && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" data-testid="rescale-prompt">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-5">
            <div className="text-base font-extrabold text-slate-900 mb-1.5">
              {ct("wizard.tokens.rescale_prompt_title", "Update your reward amounts too?")}
            </div>
            <p className="text-sm text-slate-500 mb-4 leading-snug">
              {ct("wizard.tokens.rescale_prompt_body", "You've changed what a token is worth. Your rewards can be scaled to match, so they're worth the same in {symbol} as before.", { symbol: CURRENCY_SYMBOL })}
            </p>
            <div className="flex flex-col gap-2">
              {/* Scale is the primary action (Alan): it preserves the £ value of everything
                  already set, which is almost always what's wanted. Declining is the
                  deliberate exception, so it's the quieter of the two. */}
              <button
                data-testid="rescale-accept"
                className="w-full bg-slate-900 text-white font-bold text-sm rounded-lg py-2.5"
                onClick={() => { rescalePrompt.onAccept(); setRescalePrompt(null); }}
              >
                {ct("wizard.tokens.rescale_prompt_accept", "Scale them for me")}
              </button>
              <button
                data-testid="rescale-decline"
                className="w-full bg-white border border-slate-200 text-slate-500 font-bold text-sm rounded-xl py-2.5"
                onClick={() => { rescalePrompt.onDecline(); setRescalePrompt(null); }}
              >
                {ct("wizard.tokens.rescale_prompt_decline", "Keep my amounts")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}



// Step 6 — the notification opt-in, placed last on purpose. Asking on step 1 would land
// before the parent has any children, requests, or reason to care; asking here lands right
// after they've spent five steps building something that only works if they see what their
// kids send. It also sits next to the install prompt on the completion overlay, which matters
// on iOS: installing to the Home Screen is a hard prerequisite for push there, so the two
// belong in the same stretch of the flow rather than discovered separately weeks apart.
export function NotificationSetupStep({ code, onFinish, onBack }) {
  // Journey review P1#2 + Alan's "on by default" amendment. The old pair — 'Skip & finish'
  // and '🎉 Finish setup' — were the SAME onFinish dressed as a choice. Now the toggle's
  // real status drives one honest primary: if notifications aren't on yet, Finish itself
  // requests them (a button tap satisfies the browser's user-gesture rule — as close to
  // "on by default" as platforms allow) and finishes whatever the answer; the quieter
  // text link below opts out explicitly. iOS's install-first constraint is unchanged —
  // requestPushSubscription resolves to its existing forced-off status and setup still
  // completes.
  const [pushStatus, setPushStatus] = useState("checking");
  const [finishing, setFinishing] = useState(false);
  // Can a tap on Finish plausibly turn notifications ON from here? Only from a resolved
  // "off" — or from "checking", where we don't yet know and the old behaviour (ask, then
  // finish whatever the answer) is still the right guess. "on" is already done;
  // "unsupported", "needs-install" and "denied" are states no button on this screen can
  // change, and asking for permission in them either throws or resolves straight back to
  // the same answer. Before this, the step assumed "off" for everything and pretended it
  // could (Alan, staging, 30 Aug 2026).
  //
  // "checking" counts deliberately rather than as an oversight: usePushStatus resolves
  // through navigator.serviceWorker.ready, which on a page with no registration never
  // settles at all — and a parent must never be left on a Finish screen with no visible
  // way off it because a promise didn't come back.
  const canSubscribeHere = pushStatus === "off" || pushStatus === "checking";
  async function finishWithNotifications() {
    if (finishing) return;
    setFinishing(true);
    try {
      if (canSubscribeHere) {
        try { await requestPushSubscription(code, "parent", null); } catch {}
      }
      onFinish();
    } finally {
      // Release the latch (Alan, staging, 31 Aug 2026: "the finish setup cta was blocked").
      //
      // `finishing` exists to stop a double-tap while the permission request is in flight,
      // and it was never released — harmless in the real app, where finishing the wizard
      // flips onboardingDone and unmounts this whole tree a moment later. But ?preview=wizard
      // renders SetupWizard unconditionally, so nothing unmounts: the latch stayed set, the
      // button stayed disabled at 60% opacity, and the preview could not be run a second
      // time without reloading the page. Reproduced in the jsdom harness — onFinish fires
      // once, then the button is dead — and covered by a check there now.
      setFinishing(false);
    }
  }
  return (
    <>
      <WizardIntro
        icon={<Icon id="ic-bell" size={30} />}
        title={ct("wizard.notifications.title", "Don't miss what your kids send")}
        body={ct("wizard.notifications.body", "Turn on notifications and you'll know the moment a request arrives.")}
      />
      {/* Native Run C, Part 2: one card, the same rows Settings → Notifications shows, without
          their sub-lines; the news row is native only and offered unticked, never pre-set. */}
      <NotificationGroup testId="wizard-notifications-group">
        <NotificationToggle code={code} role="parent" childId={null} variant="row" label={ct("settings.notifications.row_requests", "Requests and finished tasks")} sub={null} newsRow newsSub={false} onStatusChange={setPushStatus} />
      </NotificationGroup>
      <p className="text-xs text-slate-500 px-1">
        {ct("wizard.notifications.footnote", "You can change these any time in Settings → Notifications.")}
      </p>
      <WizardFooter>
        <WizardBackButton onBack={onBack} />
        <button className="flex-1 md:flex-none md:min-w-[180px] md:px-5 bg-slate-900 text-white font-bold text-sm rounded-lg py-2.5 disabled:opacity-60" data-testid="finish-setup" disabled={finishing} onClick={finishWithNotifications}>
          {ct("wizard.notifications.finish_button", "Finish setup")}
        </button>
      </WizardFooter>
      {/* The opt-out link is hidden only when notifications are already ON, where it would
          be offering to undo something the parent just did. Everything else keeps it.
          //
          // An earlier version of this also hid it for "unsupported" and "denied", on the
          // argument that a device which cannot subscribe makes "finish WITHOUT
          // notifications" read like a choice the parent made rather than one the browser
          // made for them. CI disagreed, loudly and correctly: 26 specs reach a set-up
          // household through completeWizardFast, which leaves the wizard by this link, and
          // headless Chromium reports permission as denied — so the only exit from the last
          // screen vanished. The refinement was not worth a conditional exit, and on a
          // denied or unsupported device the link is still literally true: they ARE
          // finishing without notifications, and the card above has said why. */}
      {pushStatus !== "on" && (
        <p className="wizard-after-footer md:text-right text-center -mt-1">
          <button className="text-xs font-bold text-slate-400 underline underline-offset-2" data-testid="finish-without-notifications" onClick={onFinish}>
            {ct("wizard.notifications.finish_without", "Finish without notifications")}
          </button>
        </p>
      )}
    </>
  );
}


/* The setup fork (LB-128, decided 31 Aug; amended 2 Sep).
 *
 * After the money step, setup forks. "Set it up for me" is the recommended, visually
 * primary route: the household is seeded from each child's cap and the parent is shown
 * what came out, child by child, with a Change link on every part. "Take me through it"
 * is a real second route — the guided walk through steps 4 and 5 — carrying an honest
 * time estimate computed from the number of children on screen, because the stated cost
 * is what makes the fork honest rather than a guess: naming ten minutes for two children
 * stops the guided route reading as the proper one and the auto route as the shortcut.
 *
 * The fork changes NAVIGATION only, never data (rider 4): both routes seed every child at
 * this tap, from one function, so a parent who backs out of the review into the guided
 * walk finds exactly the same numbers. There is no cheaper path and no dearer one.
 */
export const CUSTOM_MINUTES_PER_CHILD = 5;

export function SetupFork({ childCount, onChoose, onBack, heading, body, autoLabel }) {
  const n = Math.max(1, childCount || 1);
  const mins = n * CUSTOM_MINUTES_PER_CHILD;
  // Pass V (Alan): the routes live in a white "Kid setup" panel on the right — the line says
  // what each costs, the two buttons are the choice — and Back sits below it, outside.
  return (
    <>
      <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="setup-fork">
        {/* The add-child sheet passes its own heading/body (sibling provenance); the wizard's
            money step keeps its explanation in the intro and gets the plain panel. */}
        <div className="text-base font-extrabold text-slate-900 mb-1">{heading || ct("wizard.fork.panel_title", "Kid setup")}</div>
        {body ? <p className="text-sm text-slate-500 leading-snug mb-2">{body}</p> : null}
        <p className="text-sm text-slate-500 leading-snug mb-3">
          {n === 1
            ? ct("wizard.fork.panel_line_one", "Takes about {mins} minutes, or we can do it for you.", { mins })
            : ct("wizard.fork.panel_line_many", "Takes about {per} minutes per child, or we can do it for you.", { per: CUSTOM_MINUTES_PER_CHILD })}
        </p>
        {/* Alan, 15 Sep: on a phone the recommended "Set it up for me" sits ABOVE "Take me
            through it" (order-first while stacked). Side by side on wider screens it keeps
            the primary on the right, as before (md:order-none). DOM order is unchanged so
            existing specs and tab order are untouched. */}
        <div className="flex flex-col md:flex-row gap-2 md:justify-end">
          <button type="button" className="w-full md:w-auto bg-pill text-navy font-bold text-sm rounded-lg py-3 px-4" data-testid="wizard-route-custom" onClick={() => onChoose("custom")}>
            {ct("wizard.fork.custom", "Take me through it")}
            <span className="sr-only" data-testid="wizard-route-custom-estimate">{n === 1 ? ct("wizard.fork.estimate_one", "about {mins} minutes", { mins }) : ct("wizard.fork.estimate_many", "about {mins} minutes for {n} children", { mins, n })}</span>
          </button>
          <button type="button" className="w-full md:w-auto md:min-w-[180px] order-first md:order-none bg-slate-900 text-white font-bold text-sm rounded-lg py-3 px-5" data-testid="wizard-route-auto" onClick={() => onChoose("auto")}>
            {autoLabel || ct("wizard.fork.auto", "Set it up for me")}
          </button>
        </div>
      </div>
      <WizardFooter>
        <WizardBackButton onBack={onBack} />
      </WizardFooter>
    </>
  );
}

/* The setup review (LB-127 PR3; prototype v2 corrections carried in).
 *
 * LEADS WITH CONTENT, NOT PRICES: which habits, which tasks, which word lists and tables.
 * A parent shown only numbers has seen a budget, not a setup. Prices sit at the end of
 * each row, and the health line at the foot says what it all adds up to against the cap.
 *
 * NO REPRICE ON A FRESH REVIEW: the engine derives the four slots from the cap so they sum
 * to exactly the cap by construction. The health line's Reprice appears only once a
 * parent edit — here or in a guided part — breaks that (repriceWhen="offCap").
 *
 * EVERY PART HAS A CHANGE LINK that opens the exact guided screen for it and comes back
 * here. This is what makes auto safe to accept: a parent who wants to swap one habit does
 * not have to go back to the fork and redo everything (LB-128 rider 1).
 *
 * THE CAP IS EDITABLE HERE, because it is the one number with no safe default. Changing it
 * regenerates whatever the parent has not touched; anything they edited stays, and if that
 * leaves the child over cap the health line says so and Reprice is their tap.
 */
export function SetupReviewStep({ child, state, update, showToast, index, total, nextChild, onChange, onContinue, onBack, provenance, continueLabel, hideContext, reseed }) {
  const capPence = spendingCapPence(child);
  const capLabel = capPence === null ? "" : fmtPence(capPence);
  const habits = (state.habits || []).filter((h) => h.childId === child.id);
  const tasks = (state.activities || []).filter((a) => a.childId === child.id);
  const le = learnEarnSummary(child, state).rows;
  const fullSet = resolveStarterSets(isYoungChild(child) ? "young" : "older").tasks.length;
  const seededCount = tasks.filter((a) => a.seeded).length;
  const trimmed = capPence !== null && seededCount > 0 && seededCount < fullSet && tasks.length === seededCount;
  const capText = () => (capPence === null ? "" : String(Math.round(capPence) / 100));
  const [capDraft, setCapDraft] = useState(capText());
  useEffect(() => { setCapDraft(capText()); }, [child.id, capPence]);

  function commitCap() {
    const n = parseFloat(capDraft);
    if (!n || n <= 0) { setCapDraft(capText()); return; }
    const gbp = Math.round(n * 100) / 100;
    if (capPence !== null && Math.round(gbp * 100) === Math.round(capPence)) return;
    update((s) => {
      const c = s.profiles.find((p) => p.id === child.id);
      if (!c) return null;
      c.spendingCap = { ...(c.spendingCap || {}), enabled: true, weeklyGBP: gbp };
      // A per-child edit on a household set to "same for everyone" makes it per child,
      // which is what the Settings toggle would have done — recorded, not silent.
      if (s.settings.capMode !== "perChild" && (s.profiles || []).length > 1) s.settings.capMode = "perChild";
      // `reseed` lets the add-a-child sheet regenerate from its chosen SOURCE (a sibling
      // copy) rather than the standard set — a cap change on that review used to fall
      // through to the standard seeder and leave a stale "Based on Alex's" line with the
      // wrong "left out N" count (Alan, 4 Sep).
      (reseed || ((st, id) => seedHouseholdFromCaps(st, [id])))(s, child.id);
      return s;
    });
  }

  const changeLink = (target, testId) => (
    <button type="button" className="text-xs font-extrabold text-emerald-700 flex items-center gap-1 flex-shrink-0" data-testid={testId} onClick={() => onChange(target)}>
      <Icon id="ic-edit" size={12} />{ct("wizard.review.change", "Change")}
    </button>
  );
  const card = (iconId, title, testId, target, changeTestId, body) => (
    <div className="bg-white rounded-lg border border-slate-200 p-3.5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0"><Icon id={iconId} size={16} /></div>
        <div className="text-sm font-bold text-slate-900 flex-1 min-w-0">{title}</div>
        {changeLink(target, changeTestId)}
      </div>
      {body}
    </div>
  );

  return (
    <>
      <WizardIntro header={!hideContext ? <ChildContextBar child={child} contextLine={ct("wizard.review.child_context", "Reviewing {name}'s setup", { name: child.name })} index={index} total={total} /> : null} 
        icon={<Icon id="ic-choice" size={30} />}
        title={ct("wizard.review.title", "Here's {name}'s setup", { name: child.name })}
        body={capPence === null
          ? ct("wizard.review.body_uncapped", "Everything below is ready to go. Change anything you like, or carry on.")
          : ct("wizard.review.body", "Everything below is ready to go, sized to {cap} a week. Change anything you like, or carry on.", { cap: capLabel })}
      />
      {provenance}
      <div className="flex flex-col gap-2" data-testid="setup-review">
        {card("ic-flame", ct("wizard.review.habits_title", "Daily habits"), "review-habits", "habits", "review-change-habits", (
          <>
            {habits.length ? (
              <ul className="text-sm text-slate-700 flex flex-col gap-1">
                {habits.map((h) => <li key={h.id} className="flex items-center gap-2" data-testid="review-habit-row"><span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />{h.label}</li>)}
              </ul>
            ) : (
              <p className="text-xs text-slate-400">{ct("wizard.review.habits_none", "No daily habits yet.")}</p>
            )}
            <p className="text-xs font-medium text-slate-400 mt-2">
              {ct("wizard.review.habits_reward", "Streak reward: {tok} tokens for {days} days a week", { tok: child.habitReward ?? 0, days: child.habitThreshold ?? 5 })}
            </p>
          </>
        ))}

        {card("ic-cap", ct("wizard.review.tasks_title", "Earning tasks"), "review-tasks", "tasks", "review-change-tasks", (
          <>
            {tasks.length ? (
              <ul className="text-sm text-slate-700 flex flex-col gap-1">
                {tasks.map((a) => (
                  <li key={a.id} className="flex items-center gap-2" data-testid="review-task-row">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                    <span className="flex-1 min-w-0 truncate">{a.label}</span>
                    <span className="text-xs font-medium text-slate-500 flex-shrink-0 flex items-center gap-1"><CoinIcon className="w-3.5 h-3.5" />{a.tokens}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-slate-400">{ct("wizard.review.tasks_none", "No earning tasks yet.")}</p>
            )}
            {trimmed && (
              <p className="text-xs font-medium text-slate-400 mt-2" data-testid="review-trimmed-note">
                {ct("wizard.review.trimmed", "We kept the set small so it fits {cap} a week — add more any time.", { cap: capLabel })}
              </p>
            )}
          </>
        ))}

        {card("ic-brain", ct("wizard.review.learnearn_title", "Learn & Earn games"), "review-learnearn", "learnearn", "review-change-learnearn", (
          le.length ? (
            <ul className="text-sm text-slate-700 flex flex-col gap-1.5">
              {le.map((row) => (
                <li key={row.id} className="flex items-start gap-2" data-testid="review-learnearn-row">
                  <Icon id={row.icon} size={14} className="mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold leading-tight">{row.label}</div>
                    <div className="text-xs text-slate-400 leading-snug">{row.detail}</div>
                  </div>
                  <span className="text-xs font-medium text-slate-500 flex-shrink-0">{row.pays}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-400">{ct("wizard.review.learnearn_none", "All games are switched off.")}</p>
          )
        ))}

        <div className="bg-white rounded-lg border border-slate-200 p-3.5 flex items-center gap-3" data-testid="review-cap-card">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-slate-900">{ct("wizard.review.cap_title", "Weekly cap")}</div>
            <div className="text-xs text-slate-400 font-medium leading-snug">{ct("wizard.review.cap_hint", "The most {name} can earn in a week. Everything above is sized to it.", { name: child.name })}</div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-sm text-slate-400">{CURRENCY_SYMBOL}</span>
            <input
              type="number" inputMode="decimal" min="0.01" step="any"
              className="w-16 text-sm font-bold text-slate-900 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 text-right"
              data-testid="review-cap-input"
              value={capDraft}
              onChange={(e) => setCapDraft(e.target.value)}
              onBlur={commitCap}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            />
          </div>
        </div>

        <EarningsHealthLine child={child} state={state} update={update} showToast={showToast} repriceWhen="offCap" />
      </div>
      <WizardFooter>
        <WizardBackButton onBack={onBack} />
        <button className="flex-1 md:flex-none md:min-w-[180px] md:px-5 bg-slate-900 text-white font-bold text-sm rounded-lg py-2.5" data-testid="review-continue" onClick={onContinue}>
          {continueLabel || (nextChild ? ct("wizard.earn.next_child", "Next: {name}", { name: nextChild.name }) : ct("wizard.review.looks_good", "Looks good, continue"))}
        </button>
      </WizardFooter>
    </>
  );
}
